import { reset, SubmissionError } from 'redux-form';
import { handleActions } from 'redux-actions';
import moment from 'moment';

import reduce from 'lodash/reduce';
import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';

import createCalendar, { getDateRangeWithTimeZoneFromCalendar } from '../../shared/calendar';
import createFilters, { getFiltersObject } from '../../shared/filters';
import { createSelectOptionsFromSysEnums } from '../../shared/form-elements/select';
import http from '../../shared/http';

import endpoints from '../../../config/endpoints';

export const namespace = 'marketingFunnelActivity';

const formCalendar = createCalendar({
	namespace,
	stateNodeName: 'formCalendar',
	defaultValue: {
		start: moment().startOf('isoWeek').format('YYYY-MM-DD'),
		end: moment().endOf('isoWeek').format('YYYY-MM-DD')
	}
});

const calendar = createCalendar({
	namespace,
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(30, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace,
	stateNodeName: 'filters',
	listOfFilters: [
		createFilter('Names', 'name'),
		createFilter('Types', 'type'),
		createFilter('Tags', 'tag'),
		createFilter('Countries', 'country'),
		createFilter('Offers', 'offer'),

		createFilter('UTM: source', 'utm_source'),
		createFilter('UTM: medium', 'utm_medium'),
		createFilter('UTM: campaign', 'utm_campaign'),
		createFilter('UTM: term', 'utm_term'),
		createFilter('UTM: content', 'utm_content')
	]
});

function createFilter(title, name) {
	return {
		name,
		title,
		placeholder: `Add ${title.toLowerCase()}`,
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: `v2/marketing-funnel/activity/lookups/${name}`
		}
	};
}

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const FETCH_FORM_SUCCESS = `${namespace}/fetch-form-success`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;
const APPLY = `${namespace}/apply`;
const CLEAR = `${namespace}/clear`;

const FETCH_LOOKUPS_SUCCESS = `${namespace}/fetch-lookups-success`;
const FETCH_LOOKUPS_ERROR = `${namespace}/fetch-lookups-error`;
const REMOVE_BY_ID_SUCCESS = `${namespace}/remove-by-id-success`;
const SET_INITIAL_VALUES = `${namespace}/set-initial-values`;

// initial state
const initialState = {
	destroyOnUnmount: true,
	isLoading: true,
	isError: false,
	data: null,
	form: null,
	formCalendar: formCalendar.initialState,
	filters: filters.initialState,
	calendar: calendar.initialState
};

// actions
export const actions = {
	own: {
		fetchData: () => (dispatch, getState) => {
			request(getRequestQuery(getState), FETCH_DATA_SUCCESS)(dispatch);
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),

		updateById: (id) => (dispatch, getState) => {
			const form = find(getState()[namespace].data, { id });

			if (form) {
				dispatch({ type: FETCH_FORM_SUCCESS, payload: { data: form } });

				const range = {
					startDate: moment(form.starts_at).startOf('day'),
					endDate: moment(form.ends_at).endOf('day')
				};

				dispatch(formCalendar.actions.formCalendarChange(range, formCalendar.actions.formCalendarApply));
				dispatch(formCalendar.actions.formCalendarChange(range, formCalendar.actions.formCalendarApply));
			}
		},

		removeById: (id) => (dispatch) => {
			const request = http({
				url: `v2/marketing-funnel/activity/${id}`,
				method: 'delete',
				actions: [REMOVE_BY_ID_SUCCESS, FETCH_DATA_ERROR]
			});

			return request(dispatch).then((action) => {
				if (action.type === REMOVE_BY_ID_SUCCESS) {
					dispatch(actions.apply());
				}
			});
		},

		onSubmit: (values) => (dispatch, getState) => {
			const state = getState()[namespace];

			const range = getDateRangeWithTimeZoneFromCalendar(state.formCalendar);

			const countries = map(values.countries, 'value');
			const offers = map(values.offers, 'value');

			const payload = {
				name: values.name,
				tag: get(values, 'tag[0].value') || null,
				utm_source: values.utm_source || null,
				utm_medium: values.utm_medium || null,
				utm_campaign: values.utm_campaign || null,
				utm_term: values.utm_term || null,
				utm_content: values.utm_content || null,
				price: values.price || null,
				comment: values.comment || null,

				country_ids: !countries.length ? null : countries,
				offer_ids: !offers.length ? null : offers,

				type: get(values, 'type[0].value'),

				starts_at: range.start,
				ends_at: range.end
			};

			dispatch(actions.own.toggleProgressBar());

			const id = get(state.form, 'id');

			const request = http({
				url: `v2/marketing-funnel/activity/${id || ''}`,
				method: !id ? 'post' : 'put',
				actions: [SET_INITIAL_VALUES, FETCH_DATA_ERROR],
				data: payload
			});

			return request(dispatch).then((action) => {
				if (action.type === SET_INITIAL_VALUES) {
					dispatch(reset(`${namespace}Form`));
					dispatch(actions.apply());
				}
				if (action.type === FETCH_DATA_ERROR && action.payload.response.status === 400) {
					throw new SubmissionError(prepareFormErrors(action.payload.response.data));
				}
			});
		},

		locationsLookups: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC}?query=${text}`,
				method: 'get',
				actions: [FETCH_LOOKUPS_SUCCESS, FETCH_LOOKUPS_ERROR]
			});

			return request(dispatch).then((action) => {
				if (action.type === FETCH_LOOKUPS_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},
		offersLookups: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS}?query=${text}`,
				method: 'get',
				actions: [FETCH_LOOKUPS_SUCCESS, FETCH_LOOKUPS_ERROR]
			});

			return request(dispatch).then((action) => {
				if (action.type === FETCH_LOOKUPS_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},
		tagsLookups: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATE_TAGS}?query=${text}`,
				method: 'get',
				actions: [FETCH_LOOKUPS_SUCCESS, FETCH_LOOKUPS_ERROR]
			});

			return request(dispatch).then((action) => {
				if (action.type === FETCH_LOOKUPS_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		}
	},
	apply: () => (dispatch, getState) => {
		dispatch({ type: APPLY });
		dispatch(actions.own.fetchData());
	},
	clear: () => (dispatch, getState) => {
		dispatch({ type: CLEAR });
		dispatch(actions.own.fetchData());
	},
	formCalendar: formCalendar.actions,
	filters: filters.actions,
	calendar: calendar.actions
};

const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data,
			isLoading: false,
			isError: false
		}),
		[FETCH_FORM_SUCCESS]: (state, action) => ({
			...state,
			form: action.payload.data,
			isLoading: false,
			isError: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			destroyOnUnmount: false,
			isLoading: false,
			isError: true
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		}),
		[APPLY]: (state, action) => ({
			...state,
			data: null,
			isLoading: true
		}),
		[CLEAR]: (state, action) => initialState,

		[SET_INITIAL_VALUES]: (state, action) => ({
			...state,
			destroyOnUnmount: true,
			form: null,
			isLoading: false,
			isError: false
		})
	},
	formCalendar.reducers,
	filters.reducers,
	calendar.reducers
);

// reducers
export default handleActions(reducers, initialState);

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getInitialValues: state => {
		const form = selectors.select(state, 'form');

		return form && {
			...form,
			type: [{ value: form.type, label: form.type }],
			tag: [{ value: form.tag, label: form.tag }]
		};
	},
	getFormCalendar: state => formCalendar.selectors.getCalendar(
		state => state[namespace].formCalendar,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state[namespace].filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state[namespace].calendar,
		state => state
	)(state),
	getActivityTypes: state => createSelectOptionsFromSysEnums(state.root.lists['marketing-funnel-activity-types'])
};

function getRequestQuery(getState) {
	const state = getState()[namespace];

	const range = getDateRangeWithTimeZoneFromCalendar(state.calendar);
	const filters = getFiltersObject(state.filters, false);
	const inverse_filters = getFiltersObject(state.filters, true);

	return {
		dates: [range.start, range.end],
		filters,
		inverse_filters
	};
}

function request(query, successActionType) {
	return http({
		url: 'v2/marketing-funnel/activity',
		method: 'get',
		actions: [successActionType, FETCH_DATA_ERROR],
		query
	});
}

function prepareFormErrors(errors) {
	const fields = {
		country_ids: 'countries',
		offer_ids: 'offers'
	};

	return reduce(errors, (result, value) => {
		const field = value.dataPath.slice(1);
		return {
			...result,
			[fields[field] || field]: value.message
		};
	}, {});
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/marketing-funnel/activity/index.js